/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

'use es6';

import { Collection, Map as ImmutableMap } from 'immutable';
import PropTypes from 'prop-types';
import PortalIdParser from 'PortalIdParser';
import { useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { View } from 'foundations-components/components/View';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Link } from 'foundations-components/transitional/components/Link';
import { getId as getReportId, getName as getReportName } from '../../../data/schemas/reportSchema';
import { getId as getDashboardId, getName as getDashboardName } from '../../../data/schemas/dashboardSchema';
import { showSuccessNotification, showFailureNotification } from '../../shared/reportActionAlerts';
import DashboardSelect from '../../shared/inputFields/DashboardSelect';
import ActionSubmitButton from '../../shared/ActionSubmitButton';
import { ACTION_STATUSES } from '../../shared/actionStatuses';
import { useHideDialog } from '../../../dialog/ducks';
import { useTracker } from '../../../utils/hooks/useTracker';
import { EVENTS } from '../../../utils/usageTracker';
import { MOVE_REPORT } from '../../../data/requests/report';
import { useMutation } from 'data-fetching-client';
import { noop } from '../../shared/noop';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const showMoveSuccessNotification = (reportName, dashboardName, goToDashboard) => {
  showSuccessNotification({
    message: 'reportActions.move.success',
    options: {
      reportName,
      dashboardName
    },
    goToDashboard
  });
};
export const showMoveFailureNotification = () => {
  showFailureNotification({
    message: 'reportActions.move.failure'
  });
};
const MoveToDashboardAccessAlert = () => /*#__PURE__*/_jsx(View, {
  as: "div",
  paddingBlockStart: "400",
  children: /*#__PURE__*/_jsxs(Alert, {
    type: "warning",
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "reportActions.saveToDashboard.alert.text"
    }), ' ', /*#__PURE__*/_jsx(Link, {
      external: true,
      href: "https://knowledge.hubspot.com/account/partition-your-hubspot-assets#dashboards",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reportActions.saveToDashboard.alert.linkText"
      })
    })]
  })
});
const MoveReportModal = ({
  appUsageTracker,
  currentDashboard,
  defaultDashboardTarget,
  onMove = noop,
  report
}) => {
  const hideDialog = useHideDialog();
  const usageTracker = useTracker(appUsageTracker);
  const currentDashboardId = getDashboardId(currentDashboard);
  // defaultDashboardTarget has precedence as the selected value of the modal ONLY
  // when the user is directed to the modal from the "Add reports" flow from the dashboard view
  const [selectedDashboardId, setSelectedDashboardId] = useState(getDashboardId(defaultDashboardTarget || currentDashboard));
  const [selectedDashboardName, setSelectedDashboardName] = useState(getDashboardName(defaultDashboardTarget || currentDashboard));
  const [selectedDashboardError, setSelectedDashboardError] = useState(null);
  const [actionStatus, setActionStatus] = useState(ACTION_STATUSES.NOT_STARTED);
  const trackOnConfirm = () => {
    if (usageTracker) {
      usageTracker.track(EVENTS.onConfirmMoveReport, {
        dashboardType: 'EXISTING'
      });
    }
  };
  const [moveReportToDashboard] = useMutation(MOVE_REPORT, {
    onCompleted: () => {
      const goToDashboard = () => {
        window.location = `/reports-dashboard/${PortalIdParser.get()}/view/${selectedDashboardId}`;
      };
      showMoveSuccessNotification(getReportName(report), selectedDashboardName, goToDashboard);
      hideDialog();
      trackOnConfirm();
      onMove(selectedDashboardId);
      setActionStatus(ACTION_STATUSES.COMPLETE);
    },
    onError: error => {
      if (error instanceof Error) {
        showMoveFailureNotification();
        console.error(error);
      }
      setActionStatus(ACTION_STATUSES.FAILED);
    }
  });
  const onConfirm = () => {
    moveReportToDashboard({
      variables: {
        oldDashboardId: currentDashboardId,
        newDashboardId: selectedDashboardId,
        reportId: getReportId(report)
      }
    });
  };
  const onReject = () => {
    if (usageTracker) {
      usageTracker.track(EVENTS.addToDashboardInteraction, {
        actionType: 'edit',
        action: 'add-to-dashboard',
        assetType: 'report',
        step: 'exit'
      });
    }
    hideDialog();
  };
  return /*#__PURE__*/_jsxs(Modal, {
    width: 540,
    "data-test-id": "report-move-modal",
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onReject
      }), /*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.moveReport.moveBetweenDashboards.header"
        })
      })]
    }), /*#__PURE__*/_jsxs(DialogBody, {
      children: [/*#__PURE__*/_jsx(FormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.selectedCount.report"
        }),
        children: /*#__PURE__*/_jsx(DashboardSelect, {
          dashboardId: selectedDashboardId,
          onChange: ({
            dashboardId,
            dashboardName
          }) => {
            setSelectedDashboardId(dashboardId);
            setSelectedDashboardName(dashboardName);
          },
          onValidationError: setSelectedDashboardError,
          savedReport: report
        })
      }), /*#__PURE__*/_jsx(MoveToDashboardAccessAlert, {})]
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(Tooltip, {
        title: selectedDashboardError,
        disabled: !selectedDashboardError,
        children: /*#__PURE__*/_jsx(ActionSubmitButton, {
          use: "primary",
          onClick: () => {
            setActionStatus(ACTION_STATUSES.PENDING);
            onConfirm(selectedDashboardId, selectedDashboardName);
          },
          actionStatus: actionStatus,
          disabled: !!selectedDashboardError,
          "data-test-id": "report-actions-move",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "reporting-action-components.actions.moveReport.moveBetweenDashboards.confirm"
          })
        })
      }), /*#__PURE__*/_jsx(Button, {
        use: "secondary",
        onClick: onReject,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.moveReport.moveBetweenDashboards.reject"
        })
      })]
    })]
  });
};
MoveReportModal.propTypes = {
  appUsageTracker: PropTypes.object.isRequired,
  dashboards: PropTypes.instanceOf(Collection).isRequired,
  currentDashboard: PropTypes.instanceOf(Collection),
  defaultDashboardTarget: PropTypes.instanceOf(Collection),
  onMove: PropTypes.func,
  report: PropTypes.instanceOf(ImmutableMap).isRequired
};
export default MoveReportModal;